import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import useDataContext from "../../providers/DataProvider/useDataContext";
import { LanguageOptions } from "../../types";
import { subscribeToScroll } from "../../assets/scripts/ScrollManager";
import { LanguageIcon, CloseLanguageIcon } from "../Icons/Icons";
import "./LanguageButton.css"

interface Props {
    ariaLabel?: string;
    buttonClassName?: string;
    isHomePage?:boolean;
    isMenuOpen?: boolean;
}

const LanguageButton: React.FC<Props> = ({ ariaLabel, buttonClassName, isHomePage, isMenuOpen }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(!isHomePage);
    const { setLanguage, currentLang } = useDataContext();
    const location = useLocation();
    const navigate = useNavigate();

    // Suscribirse al evento de scroll si es la página de inicio. Esto para facilitar
    // agregar la clase 'isScrolled' (la cual permite estilos condicionales).
    useEffect(() => {
        if (isHomePage) {
            const unsuscribe = subscribeToScroll(setIsScrolled);
            return () => {
                unsuscribe();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    /* Cuando se cierra el menú responsivo, y las opciones de lenguaje están desplegadas,
       se cierra también el menu de opciones de lenguaje. */
    useEffect(() => {
        if (!isMenuOpen && isOpen) {
            setIsOpen(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMenuOpen]);


    const handleLanguageChange = (lang: LanguageOptions) => {
        // En producción, se fuerza la recarga completa de la página para que el
        // script del chatbot se reejecute sobre el botón real de la página nueva,
        // ya que cada idioma tiene su propio layout.
        const isDevelopment = import.meta.env.MODE === "development";
        const handleNavigate = (newRoute: string) => {
            if (isDevelopment) {
                navigate(newRoute);
            } else {
                window.location.href = newRoute;
            }
        };
        void setLanguage(lang, location.pathname, handleNavigate);
    }

    return ( isOpen ? (
            <div className={`relative flex justify-center items-center gap-8 lg:gap-0
                            py-4 lg:py-4 border-b-2 lg:border-b-0 rounded-b-2xl border-b-fifth-solid
                          text-fifth-solid text-lg lg:text-md`}>
                <button aria-label="close-language-change-button"
                        className="hidden lg:inline-block transition-transform duration-500 
                                ease-out hover:scale-75"
                        onClick={() => setIsOpen(false)}>
                    <CloseLanguageIcon className="size-4"/>
                </button>
                <button aria-label="spanish-change-button"
                        className={`relative lg:absolute lg:-left-8 
                                    ${ currentLang === "es" ? "language-active" : "language-option"}`}
                        onClick={() => handleLanguageChange("es")}>
                    ES
                </button>
                <button aria-label="english-change-button"
                        className={`relative lg:absolute lg:-top-4 
                                    ${ currentLang === "en" ? "language-active" : "language-option"}`}
                        onClick={() => handleLanguageChange("en")}>
                    EN
                </button>
                <button aria-label="portuguese-change-button"
                        className={`relative lg:absolute lg:top-10 
                                    ${ currentLang === "pt" ? "language-active" : "language-option"}`}
                        onClick={() => handleLanguageChange("pt")}>
                    PT
                </button>
            </div>
        ) : (
            <div className={`flex justify-center py-4 border-b-2 rounded-b-2xl lg:py-0 lg:border-b-0
                            ${ isScrolled ? "border-b-first-solid" : "border-b-fifth-solid"}`}>
                <button aria-label={ariaLabel}
                        className={buttonClassName}
                        onClick={() => setIsOpen(true)}>
                    <LanguageIcon className="size-7 transition-transform duration-500 
                                             ease-out hover:scale-75"/>
                </button>
            </div>
        )
    );
}

export default LanguageButton;
